import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, BladApi } from '../api/client';
import { useLista } from '../api/lista';
import type {
  Egzemplarz, Indeks, Lista, RaportDostepnosci, RodzajPozycjiZestawu, Skalowanie,
  Zajecia, Zestaw,
} from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  AtrapaLeku, Karta, Komunikat, Ladowanie, Plakietka, Pole, PoleTekstowe, Przelacznik,
  Przycisk, Pusto, Wybor,
} from '../components/ui';
import { PasekWspolpracy } from '../components/PasekWspolpracy';
import { nazwaZListy, useEdycjaWspolna } from '../lib/wspolna-edycja';
import { PrzyciskExcel } from '../components/PrzyciskExcel';
import { data as fData, godzina, ilosc } from '../lib/format';

const SKALOWANIE: Record<Skalowanie, string> = {
  STALA: 'stała ilość',
  NA_STANOWISKO: 'na stanowisko',
  NA_STUDENTA: 'na studenta',
};

export function Zestawy() {
  const { maUprawnienie } = useAuth();
  const mozeEdytowac = maUprawnienie('zestawy.zapis');
  const [otwarty, setOtwarty] = useState<number | null>(null);
  const [edytowany, setEdytowany] = useState<Zestaw | 'nowy' | null>(null);

  const lista = useQuery({
    queryKey: ['zestawy'], queryFn: () => api.get<{ pozycje: Zestaw[] }>('/zestawy'),
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Zestawy</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Komplet na scenariusz zajęć. Jedno kliknięcie sprawdza, czy wszystko jest — licząc dostępne, nie stan.
          </p>
        </div>
        <PrzyciskExcel dzial="zestawy" className="ml-auto" />
        {mozeEdytowac && (
          <Przycisk wariant="glowny" onClick={() => setEdytowany('nowy')}>Nowy zestaw</Przycisk>
        )}
      </div>

      {edytowany && (
        <FormularzZestawu
          key={edytowany === 'nowy' ? 'nowy' : edytowany.id}
          zestaw={edytowany === 'nowy' ? null : edytowany}
          zamknij={() => setEdytowany(null)}
        />
      )}

      {lista.isLoading ? <Ladowanie /> : (
        lista.data && lista.data.pozycje.length > 0 ? (
          <div className="flex flex-col gap-4">
            {lista.data.pozycje.map((z) => (
              <Karta
                key={z.id}
                tytul={`${z.code} — ${z.name}`}
                opis={z.description ?? undefined}
                akcje={
                  <div className="flex items-center gap-2">
                    {!z.isActive && <Plakietka>wyłączony</Plakietka>}
                    <Plakietka>{z.lines.length} pozycji</Plakietka>
                    <Przycisk wariant="cichy" className="min-h-9 px-2 text-xs"
                      onClick={() => setOtwarty(otwarty === z.id ? null : z.id)}>
                      {otwarty === z.id ? 'Zwiń' : 'Sprawdź kompletność'}
                    </Przycisk>
                    {mozeEdytowac && (
                      <Przycisk wariant="cichy" className="min-h-9 px-2 text-xs"
                        onClick={() => setEdytowany(z)}>Edytuj</Przycisk>
                    )}
                  </div>
                }
              >
                <ul className="divide-y divide-kamien-200">
                  {z.lines.map((l) => (
                    <li key={l.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5 text-sm">
                      <span className="font-mono text-xs text-kamien-500">
                        {l.equipmentUnit?.nrCsm ?? l.item?.code}
                      </span>
                      <span className="flex-1 text-kamien-800">
                        {l.equipmentUnit
                          ? `${l.equipmentUnit.item.name}${l.equipmentUnit.model ? ` — ${l.equipmentUnit.model}` : ''}`
                          : l.item?.name}
                        {l.item?.isDrugDummy && <span className="ml-2"><AtrapaLeku /></span>}
                      </span>
                      <span className="text-kamien-700">
                        {ilosc(l.quantity)} {l.item?.unit ?? 'szt.'}
                      </span>
                      <Plakietka>{SKALOWANIE[l.scaling]}</Plakietka>
                      {l.isOptional && <Plakietka ton="uwaga">opcjonalna</Plakietka>}
                    </li>
                  ))}
                </ul>
                {otwarty === z.id && <Kompletnosc kitId={z.id} />}
              </Karta>
            ))}
          </div>
        ) : (
          <Karta>
            <Pusto
              tytul="Brak zestawów"
              opis="Zestaw to lista sprzętu i materiałów na jeden scenariusz — na przykład „ALS dorosły” albo „Trudna intubacja”."
            />
          </Karta>
        )
      )}
    </div>
  );
}

/** Sprawdzenie kompletności na konkretne zajęcia albo na dowolny termin. */
function Kompletnosc({ kitId }: { kitId: number }) {
  const [classId, setClassId] = useState('');
  const [od, setOd] = useState('');
  const [doKiedy, setDoKiedy] = useState('');
  const [stanowiska, setStanowiska] = useState('');
  const [studenci, setStudenci] = useState('');
  const [raport, setRaport] = useState<RaportDostepnosci | null>(null);
  const [blad, setBlad] = useState<string | null>(null);

  const zajecia = useLista({
    queryKey: ['zajecia', 'do-zestawu'],
    queryFn: () => api.get<Lista<Zajecia>>(
      `/zajecia?od=${new Date(new Date().setHours(0, 0, 0, 0)).toISOString()}&rosnaco=true&naStronie=100`,
    ),
  });

  const sprawdz = useMutation({
    mutationFn: () => {
      const p = new URLSearchParams();
      if (classId) p.set('classId', classId);
      else {
        p.set('od', new Date(od).toISOString());
        p.set('do', new Date(doKiedy).toISOString());
        if (stanowiska) p.set('stanowiska', stanowiska);
        if (studenci) p.set('studenci', studenci);
      }
      return api.get<RaportDostepnosci>(`/zestawy/${kitId}/dostepnosc?${p}`);
    },
    onSuccess: (d) => { setRaport(d); setBlad(null); },
    onError: (e) => {
      setRaport(null);
      setBlad(e instanceof BladApi ? (e.bledyPol.od ?? e.message) : 'Nie udało się sprawdzić.');
    },
  });

  return (
    <div className="border-t border-kamien-200 bg-kamien-50 px-5 py-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="sm:col-span-2">
          <Wybor etykieta="Na zajęcia" value={classId} blad={zajecia.blad}
            onChange={(e) => setClassId(e.target.value)}>
            <option value="">— wskaż termin ręcznie —</option>
            {zajecia.pozycje.map((z) => (
              <option key={z.id} value={z.id}>
                {fData(z.startsAt)} {godzina(z.startsAt)} · {z.course.name}
                {z.groupName ? ` · ${z.groupName}` : ''}
              </option>
            ))}
          </Wybor>
        </div>
        {!classId && (
          <>
            <Pole etykieta="Od" type="datetime-local" value={od} onChange={(e) => setOd(e.target.value)} />
            <Pole etykieta="Do" type="datetime-local" value={doKiedy} onChange={(e) => setDoKiedy(e.target.value)} />
            <Pole etykieta="Stanowisk" type="number" min="1" value={stanowiska}
              onChange={(e) => setStanowiska(e.target.value)} />
            <Pole etykieta="Studentów" type="number" min="1" value={studenci}
              onChange={(e) => setStudenci(e.target.value)} />
          </>
        )}
      </div>

      <div className="mt-3">
        <Przycisk wariant="glowny" disabled={sprawdz.isPending || (!classId && (!od || !doKiedy))}
          onClick={() => sprawdz.mutate()}>
          {sprawdz.isPending ? 'Sprawdzanie...' : 'Sprawdź dostępność'}
        </Przycisk>
      </div>

      {blad && <div className="mt-3"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}

      {raport && (
        <div className="mt-4">
          <div className="mb-3">
            {raport.gotowe ? (
              <Komunikat rodzaj="sukces" tytul="Zestaw da się przygotować">
                Wszystkie wymagane pozycje są dostępne w tym terminie.
                {raport.podsumowanie.ostrzezen > 0 && ' Zwróć uwagę na ostrzeżenia poniżej.'}
              </Komunikat>
            ) : (
              <Komunikat rodzaj="blad" tytul="Zestawu nie da się skompletować">
                Brakuje {raport.podsumowanie.brakujacych} z {raport.podsumowanie.pozycji} pozycji.
              </Komunikat>
            )}
          </div>

          <div className="overflow-x-auto rounded border border-kamien-200 bg-white">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Pozycja</th>
                  <th className="px-4 py-2 text-right font-medium">Potrzeba</th>
                  <th className="px-4 py-2 text-right font-medium">Dostępne</th>
                  <th className="px-4 py-2 text-right font-medium">Brakuje</th>
                  <th className="px-4 py-2 font-medium">Uwagi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-kamien-200">
                {raport.pozycje.map((p) => (
                  <tr key={p.lineId} className={p.gotowe ? '' : 'bg-red-50/40'}>
                    <td className="px-4 py-2.5">
                      <span className="font-mono text-xs text-kamien-500">{p.kod}</span>{' '}
                      <span className="text-kamien-900">{p.nazwa}</span>
                      {p.isDrugDummy && <span className="ml-2"><AtrapaLeku /></span>}
                      {p.isOptional && <span className="ml-2 text-xs text-kamien-400">(opcjonalna)</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-kamien-900">
                      {ilosc(p.potrzeba)} {p.jednostka}
                    </td>
                    <td className="px-4 py-2.5 text-right text-kamien-700">{ilosc(p.dostepne)}</td>
                    <td className={`px-4 py-2.5 text-right font-semibold ${
                      Number(p.brakuje) > 0 ? 'text-status-brak' : 'text-status-ok'}`}>
                      {Number(p.brakuje) > 0 ? ilosc(p.brakuje) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-kamien-600">
                      {p.ostrzezenia.length > 0
                        ? <ul className="list-inside list-disc">{p.ostrzezenia.map((o, i) => <li key={i}>{o}</li>)}</ul>
                        : <span className="text-kamien-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

interface WierszDefinicji {
  klucz: string;
  kind: RodzajPozycjiZestawu;
  itemId: string;
  equipmentUnitId: string;
  quantity: string;
  scaling: Skalowanie;
  isOptional: boolean;
}

const pustaLinia = (): WierszDefinicji => ({
  klucz: crypto.randomUUID(), kind: 'MATERIAL', itemId: '', equipmentUnitId: '',
  quantity: '1', scaling: 'STALA', isOptional: false,
});

/** Cały stan formularza w jednym obiekcie — tego potrzebuje scalanie zmian dwóch osób. */
type StanZestawu = {
  code: string; name: string; description: string; isActive: boolean; linie: WierszDefinicji[];
};

/**
 * Pozycja zestawu po zapisie dostaje na serwerze nowy identyfikator (zestaw zapisuje się
 * jako komplet), więc o tożsamości decyduje treść: rodzaj i wskazany indeks lub egzemplarz.
 */
const kluczPozycji = (l: WierszDefinicji): string =>
  l.itemId || l.equipmentUnitId
    ? `${l.kind}|${l.kind === 'EQUIPMENT_UNIT' ? l.equipmentUnitId : l.itemId}`
    : `nowa|${l.klucz}`;

function FormularzZestawu({ zestaw, zamknij }: { zestaw: Zestaw | null; zamknij: () => void }) {
  const qc = useQueryClient();
  const [blad, setBlad] = useState<string | null>(null);
  const [bledy, setBledy] = useState<Record<string, string>>({});

  const kartoteka = useLista({
    queryKey: ['kartoteka', 'do-zestawu'],
    queryFn: () => api.get<Lista<Indeks>>('/kartoteka?tylkoAktywne=true&naStronie=500'),
  });
  const sprzet = useLista({
    queryKey: ['sprzet', 'do-zestawu'],
    queryFn: () => api.get<Lista<Egzemplarz>>('/sprzet?naStronie=300'),
  });

  /** „Wenflon 18G” zamiast „MATERIAL|12” w komunikacie o konflikcie. */
  const nazwaPozycji = (l: WierszDefinicji): string => {
    if (l.kind === 'EQUIPMENT_UNIT') {
      return nazwaZListy(sprzet.pozycje, l.equipmentUnitId, (e) => `${e.nrCsm} ${e.item.name}`) ?? `egzemplarz #${l.equipmentUnitId}`;
    }
    return nazwaZListy(kartoteka.pozycje, l.itemId, (i) => `${i.code} ${i.name}`) ?? `indeks #${l.itemId}`;
  };

  const edycja = useEdycjaWspolna<Zestaw, StanZestawu>({
    encja: 'zestawy', rekord: zestaw,
    pusty: { code: '', name: '', description: '', isActive: true, linie: [pustaLinia()] },
    doFormularza: (z) => ({
      code: z.code, name: z.name, description: z.description ?? '', isActive: z.isActive,
      linie: z.lines.length > 0
        ? z.lines.map((l) => ({
            klucz: String(l.id), kind: l.kind,
            itemId: String(l.item?.id ?? ''), equipmentUnitId: String(l.equipmentUnit?.id ?? ''),
            quantity: l.quantity, scaling: l.scaling, isOptional: l.isOptional,
          }))
        : [pustaLinia()],
    }),
    wczytaj: (id) => api.get<Zestaw>(`/zestawy/${id}`),
    etykiety: { code: 'Kod', name: 'Nazwa', description: 'Opis', isActive: 'Zestaw czynny', linie: 'Skład zestawu' },
    klucze: { linie: kluczPozycji },
    opisElementu: { linie: nazwaPozycji },
    pomijaneAtrybuty: ['klucz'],
    etykietyAtrybutow: { quantity: 'ilość', scaling: 'przeliczanie', isOptional: 'opcjonalna', kind: 'rodzaj' },
    pokazWartosc: (pole, v) => {
      if (pole === 'scaling') return SKALOWANIE[v as Skalowanie];
      if (pole === 'isOptional') return v ? 'tak' : 'nie';
      return undefined;
    },
  });
  const { dane } = edycja;
  const linie = dane.linie;
  // Cienkie nakładki, żeby reszta formularza została taka, jak była.
  const naglowek = { code: dane.code, name: dane.name, description: dane.description, isActive: dane.isActive };
  const setNaglowek = (n: typeof naglowek) => edycja.ustaw((d) => ({ ...d, ...n }));
  const setLinie = (f: WierszDefinicji[] | ((s: WierszDefinicji[]) => WierszDefinicji[])) =>
    edycja.ustaw((d) => ({ ...d, linie: typeof f === 'function' ? f(d.linie) : f }));

  const zapisz = useMutation({
    mutationFn: () => edycja.zapisz((d, wersja) => {
      const cialo = {
        code: d.code, name: d.name, description: d.description, isActive: d.isActive,
        ...(zestaw ? { version: wersja } : {}),
        pozycje: d.linie
          .filter((l) => (l.kind === 'EQUIPMENT_UNIT' ? l.equipmentUnitId : l.itemId))
          .map((l, i) => ({
            kind: l.kind,
            itemId: l.kind === 'EQUIPMENT_UNIT' ? null : Number(l.itemId),
            equipmentUnitId: l.kind === 'EQUIPMENT_UNIT' ? Number(l.equipmentUnitId) : null,
            quantity: Number(l.quantity.replace(',', '.')),
            scaling: l.scaling, isOptional: l.isOptional, sortOrder: i,
          })),
      };
      return zestaw ? api.put<Zestaw>(`/zestawy/${zestaw.id}`, cialo) : api.post<Zestaw>('/zestawy', cialo);
    }),
    onSuccess: (zapisany) => {
      void qc.invalidateQueries({ queryKey: ['zestawy'] });
      edycja.poZapisie(zapisany, zamknij);
    },
    onError: (e) => {
      if (e instanceof BladApi) {
        setBledy(e.bledyPol);
        setBlad(Object.keys(e.bledyPol).length === 0 ? e.message : e.message);
      }
    },
  });

  const materialy = kartoteka.pozycje.filter((i) => i.type === 'MATERIAL');
  const typySprzetu = kartoteka.pozycje.filter((i) => i.type === 'EQUIPMENT');

  return (
    <Karta tytul={zestaw ? `Zestaw ${zestaw.code}` : 'Nowy zestaw'}>
      <form className="flex flex-col gap-5 px-5 py-5"
        onSubmit={(e) => { e.preventDefault(); setBlad(null); setBledy({}); zapisz.mutate(); }}>
        {blad && <Komunikat rodzaj="blad">{blad}</Komunikat>}
        <PasekWspolpracy edycja={edycja} />

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Pole etykieta="Kod" value={naglowek.code} blad={bledy.code}
            onChange={(e) => setNaglowek({ ...naglowek, code: e.target.value })} required />
          <div className="lg:col-span-2">
            <Pole etykieta="Nazwa" value={naglowek.name} blad={bledy.name}
              onChange={(e) => setNaglowek({ ...naglowek, name: e.target.value })} required />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <PoleTekstowe etykieta="Opis" value={naglowek.description}
              onChange={(e) => setNaglowek({ ...naglowek, description: e.target.value })} />
          </div>
          <Przelacznik etykieta="Zestaw czynny" checked={naglowek.isActive}
            onChange={(e) => setNaglowek({ ...naglowek, isActive: e.target.checked })} />
        </div>

        <div className="flex flex-col gap-3 border-t border-kamien-200 pt-4">
          <p className="text-sm font-medium text-kamien-700">Skład zestawu</p>
          {linie.map((l, i) => (
            <div key={l.klucz} className="grid gap-3 rounded border border-kamien-200 p-3 sm:grid-cols-2 lg:grid-cols-5">
              <Wybor etykieta={`Rodzaj ${i + 1}`} value={l.kind}
                onChange={(e) => setLinie((s) => s.map((x) => x.klucz === l.klucz
                  ? { ...x, kind: e.target.value as RodzajPozycjiZestawu, itemId: '', equipmentUnitId: '',
                      scaling: e.target.value === 'EQUIPMENT_UNIT' ? 'STALA' : x.scaling }
                  : x))}>
                <option value="MATERIAL">Materiał</option>
                <option value="EQUIPMENT_TYPE">Sprzęt (dowolny egzemplarz)</option>
                <option value="EQUIPMENT_UNIT">Sprzęt (konkretny egzemplarz)</option>
              </Wybor>

              <div className="lg:col-span-2">
                {l.kind === 'EQUIPMENT_UNIT' ? (
                  <Wybor etykieta="Egzemplarz" value={l.equipmentUnitId}
                    blad={bledy[`pozycje.${i}.equipmentUnitId`] ?? sprzet.blad}
                    onChange={(e) => setLinie((s) => s.map((x) => x.klucz === l.klucz
                      ? { ...x, equipmentUnitId: e.target.value } : x))}>
                    <option value="">— wybierz —</option>
                    {sprzet.pozycje.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.nrCsm} — {e.item.name}{e.model ? ` (${e.model})` : ''}
                      </option>
                    ))}
                  </Wybor>
                ) : (
                  <Wybor etykieta="Pozycja" value={l.itemId}
                    blad={bledy[`pozycje.${i}.itemId`] ?? kartoteka.blad}
                    onChange={(e) => setLinie((s) => s.map((x) => x.klucz === l.klucz
                      ? { ...x, itemId: e.target.value } : x))}>
                    <option value="">— wybierz —</option>
                    {(l.kind === 'MATERIAL' ? materialy : typySprzetu).map((m) => (
                      <option key={m.id} value={m.id}>{m.code} — {m.name}</option>
                    ))}
                  </Wybor>
                )}
              </div>

              <Pole etykieta="Ilość" value={l.quantity} inputMode="decimal"
                blad={bledy[`pozycje.${i}.quantity`]}
                onChange={(e) => setLinie((s) => s.map((x) => x.klucz === l.klucz
                  ? { ...x, quantity: e.target.value } : x))} />

              <Wybor etykieta="Przeliczanie" value={l.scaling}
                blad={bledy[`pozycje.${i}.scaling`]}
                disabled={l.kind === 'EQUIPMENT_UNIT'}
                onChange={(e) => setLinie((s) => s.map((x) => x.klucz === l.klucz
                  ? { ...x, scaling: e.target.value as Skalowanie } : x))}>
                {Object.entries(SKALOWANIE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Wybor>

              <div className="flex items-end justify-between gap-2 sm:col-span-2 lg:col-span-5">
                <Przelacznik etykieta="Pozycja opcjonalna"
                  opis="Jej brak nie zablokuje przygotowania zestawu"
                  checked={l.isOptional}
                  onChange={(e) => setLinie((s) => s.map((x) => x.klucz === l.klucz
                    ? { ...x, isOptional: e.target.checked } : x))} />
                <Przycisk type="button" wariant="cichy" className="min-h-9 text-xs"
                  onClick={() => setLinie((s) => s.length > 1
                    ? s.filter((x) => x.klucz !== l.klucz) : [pustaLinia()])}>
                  Usuń
                </Przycisk>
              </div>
            </div>
          ))}
          <div>
            <Przycisk type="button" onClick={() => setLinie((s) => [...s, pustaLinia()])}>
              Dodaj pozycję
            </Przycisk>
          </div>
        </div>

        <div className="flex gap-3 border-t border-kamien-200 pt-4">
          <Przycisk type="submit" wariant="glowny" disabled={zapisz.isPending}>
            {zapisz.isPending ? 'Zapisywanie...' : zestaw ? 'Zapisz zmiany' : 'Utwórz zestaw'}
          </Przycisk>
          <Przycisk type="button" wariant="cichy" onClick={zamknij}>Anuluj</Przycisk>
        </div>
      </form>
    </Karta>
  );
}
